import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Medal, BookOpen, Scroll, Users, Heart } from 'lucide-react';
import SimpleHeader from '../components/SimpleHeader.jsx';
import Footer from '../components/Footer.jsx';

const TeachersPage = () => {
  const calculatePracticeYears = (startYear) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // If we're before September, subtract one year from experience
    return currentMonth < 8 ? currentYear - startYear : currentYear - startYear;
  };

  const teachers = [
    {
      id: 1,
      name: "Jean-Louis CHAUDESSOLLE",
      title: "Professeur Principal",
      rank: "5ème Dan",
      diploma: "Diplôme d'État",
      experience: `${calculatePracticeYears(1986)} ans de pratique, ${calculatePracticeYears(2000)} ans d'enseignement`, // Started practice in September 1984
      courses: ["Mardi 20:00-21:00 (Tous niveaux)", "Vendredi 20:30-22:00 (Confirmés)"],
      philosophy: "Les Arts Martiaux et l'Aïkido en particulier sont avant tout des activités nous permettant une certaine cohérence dans nos actions de la vie quotidienne. Par la pratique de techniques plus ou moins complexes, ils nous permettent d'acquérir une certaine sérénité vis-à-vis du monde qui nous entoure et donc un meilleur contrôle de nos émotions. Mon approche privilégie la compréhension profonde des principes fondamentaux.",
      background: "Jean-Louis a commencé l'Aïkido à Vincennes en 1986 avec Maître Christian Tissier, actuellement 8ème DAN. Il poursuit son apprentissage dans différents stages avec entre autres des hauts gradés, qui ont tous une approche très différente de la discipline. Il pratique ainsi l'Aïkido depuis presque 40 ans, pratique qu'il a enrichie par son expérience dans d'autres arts de combats, notamment le karaté, le full contact, la canne de combat. Il a dévoué ces 25 dernières années à l'enseignement, ce qui lui a permis d'assurer la continuité dans la progression de ses élèves, qu'il a accompagnés du 1er au 4ème DAN."
      //philosophy: "L'aïkido est avant tout un art de vivre qui nous apprend à être en harmonie avec nous-mêmes et avec les autres. Mon approche privilégie la compréhension profonde des principes fondamentaux.",
      //background: "Jean-Louis a commencé l'Aïkido à Paris avec Christian Tissier en 1984. Il pratique ainsi l'Aïkido depuis plus de 40 ans, pratique qu'il a enrichie par son expérience dans d'autres arts martiaux, notamment la canne de combat. Il a dévoué ces trentes dernières années à Cahors, ce qui lui a permis d'assurer la continuité dans la progression de ses élèves, qu'il a accompagné jusqu'au 1er, 2ème, voire 4ème DAN pour certains."
    },
    {
      id: 2,
      name: "Quentin POIRIER",
      title: "Professeur Adjoint",
      rank: "2ème Dan",
      diploma: "Brevet Fédéral",
      experience: `${calculatePracticeYears(2003)} ans de pratique, ${calculatePracticeYears(2023)} ans d'enseignement`, // Started practice in September 2003
      courses: ["Mardi 18:00-19:00 (Enfants)", "Mardi 19:00-20:00 (Débutants)", "Jeudi 18:30-19:45 (Tous niveaux)"],
      philosophy: "Le dojo doit être une bulle de respiration dans un quotidien toujours plus chaotique. L'Aïkido nous offre un espace pour souffler, nous exprimer et grandir.",
      background: "Formé à Cahors par Jean-Louis depuis l'enfance, Quentin apporte un regard neuf à l'enseignement. Jeune professeur enthousiaste, il tente de créer un espace alternatif pour un apprentissage bienveillant."
    }
  ];

  return (
    <div className="min-h-screen">
      <SimpleHeader />
      
      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: '40vh', paddingTop: '120px' }}>
        <div className="container">
          <div className="text-center">
            {/* Back to Home Link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 mb-8 text-decoration-none transition-all duration-300 hover:transform hover:translate-x-1"
              style={{ color: 'var(--brand-primary)' }}
            >
              <ArrowLeft size={20} />
              <span className="body-medium font-semibold">Retour à l'accueil</span>
            </Link>
            
            <h1 className="brand-display mb-6" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Nos Professeurs</h1>
            <p className="body-large max-w-3xl mx-auto" style={{ color: 'var(--text-on-light)' }}>
              Découvrez nos enseignants passionnés qui vous accompagneront dans votre apprentissage de l'Aïkido. 
              Leur expérience et leur dévouement font la richesse de notre dojo depuis plus de 40 ans.
            </p>
          </div>
        </div>
      </section>

      {/* Teachers Section */}
      <main>
        <section className="section-padding section-light">
          <div className="container">
            <div className="space-y-16">
              {teachers.map((teacher, index) => (
                <div 
                  key={teacher.id}
                  id={`professor-${teacher.name.toLowerCase().replace(/\s+/g, '-').replace(/[éèê]/g, 'e').replace(/[àâ]/g, 'a').replace(/[ôö]/g, 'o').replace(/[ùûü]/g, 'u').replace(/[îï]/g, 'i').replace(/ç/g, 'c').replace(/[^a-z0-9-]/g, '')}`}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Teacher Image */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div 
                      className="aspect-[4/5] rounded-lg shadow-lg overflow-hidden"
                      style={{ 
                        border: '2px solid var(--border-light)'
                      }}
                    >
                      <img 
                        src={
                          teacher.id === 1 
                            ? "/images/gallery/2018/smssecure-2019-04-03-204426-3.jpg"
                            : "/images/gallery/2024/forum/DSC03847 (2).jpg"
                        }
                        alt={`Portrait de ${teacher.name}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Teacher Info */}
                  <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div>
                      <h2 className="heading-2 mb-2" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>{teacher.name}</h2>
                      <div className="flex items-center gap-4 mb-4">
                        <span 
                          className="inline-flex items-center gap-2 px-3 py-1 rounded-full body-small font-semibold"
                          style={{ 
                            backgroundColor: 'var(--brand-primary)', 
                            color: 'white' 
                          }}
                        >
                          <Medal size={16} />
                          {teacher.rank}
                        </span>
                        <span 
                          className="inline-flex items-center gap-2 px-3 py-1 rounded-full body-small font-semibold"
                          style={{ 
                            backgroundColor: 'white', 
                            color: 'var(--brand-primary)' 
                          }}
                        >
                          <Scroll size={16} />
                          {teacher.diploma}
                        </span>
                      </div>
                      <p className="body-medium font-semibold" style={{ color: 'var(--brand-primary)' }}>
                        {teacher.experience}
                      </p>
                    </div>

                    <div>
                      <h3 className="heading-3 mb-3 flex items-center gap-2" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                        <Heart size={20} />
                        Philosophie
                      </h3>
                      <p className="body-medium italic" style={{ color: 'var(--text-on-light)' }}>
                        "{teacher.philosophy}"
                      </p>
                    </div>

                    <div>
                      <h3 className="heading-3 mb-3" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Cours assurés</h3>
                      <ul className="space-y-2">
                        {teacher.courses.map((course, idx) => (
                          <li 
                            key={idx}
                            className="flex items-center gap-3 body-medium"
                            style={{ color: 'var(--text-on-light)' }}
                          >
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: 'var(--brand-primary)' }}
                            ></div>
                            {course}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <p className="body-medium" style={{ color: 'var(--text-secondary-light)' }}>
                        {teacher.background}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="section-padding section-dark">
          <div className="container">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="heading-2 mb-6" style={{ color: 'var(--text-on-dark)', hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                Rencontrez nos professeurs
              </h2>
              <p className="body-medium mb-8" style={{ color: 'var(--text-secondary-dark)' }}>
                Venez découvrir l'enseignement de nos professeurs lors d'un cours d'essai gratuit. 
                Ils seront ravis de répondre à toutes vos questions et de vous guider dans vos premiers pas en Aïkido.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/#pricing" className="btn-primary">
                  Essai Gratuit
                </Link>
                <Link to="/#location" className="btn-secondary">
                  Nous Rendre Visite
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TeachersPage;
